import { DiscountAmoutType, FlightDiscountForm } from "@/types";
import { cn } from "@/utils/utils";
import FormField from "../common/FormField";
import DiscountAmountField from "../editDiscount/DiscountAmountField";
import DateField from "../editDiscount/DateField";
import { CircularProgress } from "@mui/material";
import { DateRange } from "react-day-picker";

interface EditDiscountFormProps {
  values: FlightDiscountForm;
  errors: Record<string, string>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateChange: (date: DateRange | undefined) => void;
  selectedDiscountType: DiscountAmoutType;
  handleDiscountTypeChange: (type: DiscountAmoutType) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isMobile: boolean;
  isLoading: boolean;
  submitForm: () => void;
  onCancel: () => void;
}

const EditFlightDiscountForm = ({
  values,
  errors,
  handleChange,
  handleDateChange,
  selectedDiscountType,
  handleDiscountTypeChange,
  isOpen,
  setIsOpen,
  isMobile,
  isLoading,
  submitForm,
  onCancel,
}: EditDiscountFormProps) => {
  const baseInputClassName =
    "placeholder:text-[#94A3B8] text-sm h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-[#020617] outline-none placeholder:text-sm placeholder:font-normal";

  return (
    <div className="mt-7 flex flex-col rounded-3xl bg-white p-5 text-[#1E293B] max-sm:mt-3 lg:px-8 lg:py-6">
      <div className="space-y-8 max-sm:space-y-5">
        <div className="text-xl font-semibold text-neutral-950 max-sm:text-lg">
          Condition
        </div>
        <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
          <div className="flex-1">
            <FormField
              label="Discount Name"
              id="discountName"
              name="discountName"
              value={values.discountName}
              onChange={handleChange}
              error={errors.discountName}
              placeholder="Enter discount name"
            />
          </div>
          <div className="flex-1">
            <FormField
              label="Discount Code"
              id="discountCode"
              name="discountCode"
              value={values.discountCode}
              onChange={handleChange}
              error={errors.discountCode}
              placeholder="Enter discount code"
              className="uppercase placeholder:normal-case"
            />
          </div>
        </div>

        <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
          <div className="flex-1">
            <div>
              <FormField
                label="Usage per Code"
                id="usageLimit"
                name="usageLimit"
                value={values.usageLimit || ""}
                onChange={handleChange}
                error={errors.usageLimit}
                placeholder="Enter usage per Code"
                type="number"
              />
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]">
              If left empty, the discount can be applied an unlimited number of
              times.
            </div>
          </div>
          <div className="flex-1">
            <div>
              <FormField
                label="Usage per User"
                id="usageLimitPerUser"
                name="usageLimitPerUser"
                value={values.usageLimitPerUser || ""}
                onChange={handleChange}
                error={errors.usageLimitPerUser}
                placeholder="Enter usage per User"
                type="number"
              />
            </div>
            <div className="mt-3 flex items-center gap-2 text-sm text-[#64748B]">
              If left empty, the discount can be applied an unlimited number of
              times.
            </div>
          </div>
        </div>

        <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
          <div className="flex-1">
            <FormField
              label="Minimum Booking Amount"
              id="minimumSpend"
              name="minimumSpend"
              value={values.minimumSpend?.toString() ?? ""}
              onChange={handleChange}
              error={errors.minimumSpend}
              placeholder="Enter amount"
              type="number"
            >
              <div
                className={cn(baseInputClassName, "relative overflow-hidden")}
              >
                <input
                  id="minimumSpend"
                  type="number"
                  name="minimumSpend"
                  placeholder="Enter amount"
                  className="h-full w-[90%] outline-none placeholder:text-[#94A3B8] bg-white"
                  onChange={handleChange}
                  value={values.minimumSpend as string | number | undefined}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex w-[68px] items-center justify-center border-l bg-[#F6F9FB] max-sm:w-12">
                  <span className="text-[16px] font-semibold text-[#94A3B8]">
                    USD
                  </span>
                </div>
              </div>
            </FormField>
          </div>
          <div className="flex-1">
            <DateField
              error={errors.startDate}
              isMobile={isMobile}
              date={{
                from: values.startDate
                  ? new Date(values.startDate)
                  : new Date(),
                to: values.endDate ? new Date(values.endDate) : new Date(),
              }}
              onDateChange={handleDateChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-12 space-y-6 max-sm:mt-4 max-sm:space-y-5">
        <div className="text-xl font-semibold text-neutral-950">Action</div>
        <div className="flex gap-8 max-sm:flex-col max-sm:gap-5">
          <div className="flex-1">
            <DiscountAmountField
              value={values.discountValue}
              onChange={handleChange}
              error={errors.discountValue}
              selectedType={selectedDiscountType}
              onTypeChange={handleDiscountTypeChange}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />
          </div>
          <div className="flex-1">
            <FormField
              label="Maximum Discount"
              id="maximumDiscountValue"
              name="maximumDiscountValue"
              value={values?.maximumDiscountValue?.toString() ?? ""}
              onChange={handleChange}
              error={errors.maximumDiscountValue}
              placeholder="Enter maximum amount"
              type="number"
              className={cn(
                selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT
                  ? "cursor-not-allowed bg-neutral-50"
                  : "",
              )}
              disabled={selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT}
            >
              <input
                id="maximumDiscountValue"
                type="number"
                name="maximumDiscountValue"
                placeholder="Enter maximum amount"
                className={cn(
                  baseInputClassName,
                  selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT
                    ? "cursor-not-allowed bg-neutral-50"
                    : "",
                )}
                onChange={handleChange}
                value={
                  values.maximumDiscountValue as string | number | undefined
                }
                disabled={
                  selectedDiscountType === DiscountAmoutType.FIXED_AMOUNT
                }
                step="0.01"
                min="0.01"
              />
            </FormField>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-4">
          <button
            className="h-12 w-[171px] rounded-[10px] bg-button-background text-base font-medium text-text outline-none max-sm:h-10 max-sm:w-1/2"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            onClick={submitForm}
            className="flex h-12 w-[191px] items-center justify-center rounded-xl bg-text text-white max-sm:h-10 max-sm:w-1/2"
            disabled={isLoading}
          >
            <span className="text-base font-medium">
              Update Discount
              {isLoading && (
                <CircularProgress
                  size="16px"
                  className="ml-3"
                  color="inherit"
                />
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFlightDiscountForm;
